import { useState, useEffect } from 'react';
import {
    Modal, Box, Typography, Button, TextField, CircularProgress, Alert,
    FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput, Chip
} from '@mui/material';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 450,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

// Hardcoded LOB options. TODO: replace with /lobs endpoint once available on stg.
const LOB_OPTIONS = ['HW', 'DC', 'DB'];

export default function AddCustomerModal({ open, onClose, onSubmit, mode = 'add', initialData = null }) {
    const isEdit = mode === 'edit';
    const [clientName, setClientName] = useState('');
    const [clientCode, setClientCode] = useState('');
    const [lobList, setLobList] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setClientName(initialData?.ClientName || '');
            setClientCode(initialData?.ClientCode || '');
            setLobList(initialData?.LOB_list || []);
            setError('');
        }
    }, [open, initialData]);

    const handleLobChange = (event) => {
        const value = event.target.value;
        setLobList(typeof value === 'string' ? value.split(',') : value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!isEdit && (!clientName || !clientCode)) {
            setError('Client Name and Client Code are required.');
            return;
        }
        if (!isEdit && lobList.length === 0) {
            setError('At least one LOB must be assigned for new customers.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await onSubmit({ clientName, clientCode, lobList });
        } catch (e) {
            setError(e.message || 'An unexpected error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle} component="form" onSubmit={handleSubmit}>
                <Typography variant="h6" component="h2">
                    {isEdit ? 'Edit Customer LOBs' : 'Add New Customer'}
                </Typography>
                {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
                <TextField
                    label="Client Name"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    fullWidth
                    required={!isEdit}
                    disabled={isEdit}
                    sx={{ mt: 2 }}
                />
                <TextField
                    label="Client Code"
                    value={clientCode}
                    onChange={(e) => setClientCode(e.target.value)}
                    fullWidth
                    required={!isEdit}
                    disabled={isEdit}
                    sx={{ mt: 2 }}
                />
                <FormControl fullWidth sx={{ mt: 2 }} required={!isEdit}>
                    <InputLabel id="lob-list-label">LOB List{!isEdit ? ' *' : ''}</InputLabel>
                    <Select
                        labelId="lob-list-label"
                        multiple
                        value={lobList}
                        onChange={handleLobChange}
                        input={<OutlinedInput label={`LOB List${!isEdit ? ' *' : ''}`} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((v) => <Chip key={v} label={v} size="small" />)}
                            </Box>
                        )}
                    >
                        {LOB_OPTIONS.map((lob) => (
                            <MenuItem key={lob} value={lob}>
                                <Checkbox checked={lobList.indexOf(lob) > -1} />
                                <ListItemText primary={lob} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                        {isSubmitting ? <CircularProgress size={24} /> : (isEdit ? 'Save' : 'Submit')}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
